import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Trash2, Edit3, Eye, EyeOff, Loader2, GripVertical, Link as LinkIcon, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

const ICON_OPTIONS = [
  { value: "", label: "None" },
  { value: "rocket", label: "Rocket" },
  { value: "calendar", label: "Calendar" },
  { value: "star", label: "Star" },
  { value: "zap", label: "Lightning" },
  { value: "megaphone", label: "Megaphone" },
  { value: "gift", label: "Gift" },
  { value: "alert", label: "Alert" },
  { value: "fire", label: "Fire" },
];

const ICON_EMOJI = {
  rocket: "🚀", calendar: "📅", star: "⭐", zap: "⚡", megaphone: "📢", gift: "🎁", alert: "🔔", fire: "🔥"
};

export default function AdminAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/announcements/admin/list`, getAuth());
      setAnnouncements(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    await axios.delete(`${API}/announcements/admin/${id}`, getAuth());
    fetchData();
  };

  const handleToggle = async (item) => {
    await axios.put(`${API}/announcements/admin/${item.announcement_id}`, { is_active: !item.is_active }, getAuth());
    fetchData();
  };

  if (loading) return <div className="flex items-center gap-2 text-gray-500"><Loader2 size={16} className="animate-spin" /> Loading...</div>;

  return (
    <div data-testid="admin-announcements">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-gray-900">Announcements Ticker</h1>
          <p className="text-sm text-gray-500 font-dm-sans mt-1">Scrolling messages displayed at the top of the landing page</p>
        </div>
        <button
          data-testid="add-announcement-btn"
          onClick={() => { setEditItem(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-black text-white px-4 py-2.5 text-sm font-dm-sans font-bold hover:bg-gray-800 transition-colors rounded-md"
        >
          <Plus size={16} /> Add Announcement
        </button>
      </div>

      {/* Preview */}
      {announcements.filter(a => a.is_active).length > 0 && (
        <div className="mb-6 bg-gray-900 rounded-lg p-3 overflow-hidden">
          <p className="text-[10px] text-gray-500 font-dm-sans uppercase tracking-wider mb-2">Preview</p>
          <div className="flex gap-8 text-cyan-400 text-sm font-dm-sans whitespace-nowrap animate-marquee-preview">
            {[...announcements.filter(a => a.is_active), ...announcements.filter(a => a.is_active)].map((a, idx) => (
              <span key={`${a.announcement_id}-${idx}`} className="flex-shrink-0">
                {a.icon && ICON_EMOJI[a.icon] ? `${ICON_EMOJI[a.icon]} ` : ""}{a.text}
                {a.link_text && <span className="text-cyan-300 ml-1 underline">{a.link_text}</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {announcements.length === 0 && (
        <div className="text-center py-12 text-gray-400 font-dm-sans">No announcements. Add one to display on the landing page.</div>
      )}

      <div className="space-y-2">
        {announcements.map(item => (
          <div key={item.announcement_id} className={`bg-white border rounded-lg px-5 py-4 flex items-center gap-4 ${item.is_active ? "border-gray-200" : "border-gray-100 opacity-60"}`}>
            <GripVertical size={16} className="text-gray-300 flex-shrink-0" />
            {item.icon && ICON_EMOJI[item.icon] && (
              <span className="text-lg flex-shrink-0">{ICON_EMOJI[item.icon]}</span>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-dm-sans font-medium text-gray-900 truncate">{item.text}</p>
              {item.link_url && (
                <p className="text-xs text-blue-500 font-dm-sans truncate mt-0.5 flex items-center gap-1">
                  <LinkIcon size={10} /> {item.link_text || item.link_url}
                </p>
              )}
            </div>
            <span className={`text-[10px] font-dm-sans font-bold px-2 py-0.5 rounded-full ${item.is_active ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-400"}`}>
              {item.is_active ? "Active" : "Inactive"}
            </span>
            <button onClick={() => handleToggle(item)} className="p-1.5 text-gray-400 hover:text-gray-700" data-testid={`toggle-${item.announcement_id}`}>
              {item.is_active ? <Eye size={15} /> : <EyeOff size={15} />}
            </button>
            <button onClick={() => { setEditItem(item); setShowForm(true); }} className="p-1.5 text-gray-400 hover:text-gray-700" data-testid={`edit-${item.announcement_id}`}>
              <Edit3 size={15} />
            </button>
            <button onClick={() => handleDelete(item.announcement_id)} className="p-1.5 text-gray-400 hover:text-red-500" data-testid={`delete-${item.announcement_id}`}>
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>

      <AnnouncementFormDialog
        open={showForm}
        onClose={() => { setShowForm(false); setEditItem(null); }}
        item={editItem}
        nextOrder={announcements.length + 1}
        onSaved={fetchData}
      />
    </div>
  );
}

function AnnouncementFormDialog({ open, onClose, item, nextOrder, onSaved }) {
  const [form, setForm] = useState({ text: "", icon: "", link_url: "", link_text: "", is_active: true, order: nextOrder });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({ text: item.text || "", icon: item.icon || "", link_url: item.link_url || "", link_text: item.link_text || "", is_active: item.is_active, order: item.order || 0 });
    } else {
      setForm({ text: "", icon: "", link_url: "", link_text: "", is_active: true, order: nextOrder });
    }
  }, [item, nextOrder]);

  const handleSave = async () => {
    if (!form.text.trim()) return;
    setSaving(true);
    try {
      if (item) {
        await axios.put(`${API}/announcements/admin/${item.announcement_id}`, form, getAuth());
      } else {
        await axios.post(`${API}/announcements/admin/create`, form, getAuth());
      }
      onSaved();
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="font-outfit text-gray-900">{item ? "Edit Announcement" : "Add Announcement"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Message Text *</label>
            <textarea
              data-testid="announcement-text-input"
              value={form.text}
              onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
              className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-md h-20 resize-none"
              placeholder="e.g. New AI Masterclass — Register Now!"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Icon</label>
            <div className="flex flex-wrap gap-2">
              {ICON_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, icon: opt.value }))}
                  className={`px-3 py-1.5 text-xs font-dm-sans rounded-md border transition-colors ${
                    form.icon === opt.value ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                  }`}
                >
                  {opt.value && ICON_EMOJI[opt.value] ? `${ICON_EMOJI[opt.value]} ` : ""}{opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Link URL</label>
              <input
                value={form.link_url}
                onChange={e => setForm(f => ({ ...f, link_url: e.target.value }))}
                className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-md"
                placeholder="/ai-tools or https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Link Text</label>
              <input
                value={form.link_text}
                onChange={e => setForm(f => ({ ...f, link_text: e.target.value }))}
                className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-md"
                placeholder="Register Now"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="block text-sm font-medium text-gray-700 font-dm-sans">Order</label>
            <input
              type="number" min="0" value={form.order}
              onChange={e => setForm(f => ({ ...f, order: parseInt(e.target.value) || 0 }))}
              className="w-20 border border-gray-300 focus:border-black px-3 py-2 text-sm font-dm-sans outline-none rounded-md"
            />
            <label className="flex items-center gap-2 ml-auto cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} className="rounded" />
              <span className="text-sm font-dm-sans text-gray-700">Active</span>
            </label>
          </div>
          <button
            data-testid="save-announcement-btn"
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-black text-white font-outfit font-bold py-2.5 hover:bg-gray-800 transition-colors disabled:opacity-60 rounded-md"
          >
            {saving ? "Saving..." : (item ? "Update" : "Create Announcement")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
